import { parseCellId, type CardType, type GameState, type PlayerId, type Side } from '@cards/engine';
import { getCardMechanicHelp, getCardPresentation, type MechanicHelp } from '@cards/cards';

export function cardName(defId: string): string {
  return getCardPresentation(defId)?.name ?? defId;
}

export function cardDescription(defId: string): string | undefined {
  return getCardPresentation(defId)?.description;
}

export type CardKeywordHelp = MechanicHelp;

export function cardKeywordHelp(defId: string, keywords: readonly string[] = []): readonly CardKeywordHelp[] {
  return getCardMechanicHelp(defId, keywords);
}

export function cardTypeLabel(type: CardType | undefined): string {
  switch (type) {
    case 'UNIT':
      return 'Боец';
    case 'STRUCTURE':
      return 'Структура';
    case 'SPELL':
      return 'Заклинание';
    case 'REACTION':
      return 'Реакция';
    case 'VIRUS':
      return 'Вирус';
    case 'EVENT':
      return 'Событие';
    case 'ITEM':
      return 'Предмет';
    case 'EFFECT':
      return 'Эффект';
    default:
      return '';
  }
}

export function phaseLabel(phase: GameState['phase']): string {
  switch (phase) {
    case 'planning':
      return 'планирование';
    case 'resolution':
      return 'разыгрывание';
    default:
      return String(phase);
  }
}

export type PlayerNames = Readonly<Partial<Record<PlayerId, string>>>;

/** A player-facing label must never expose the transport/session identifier. */
export function playerDisplayName(
  playerId: PlayerId | 'draw',
  viewerId?: PlayerId,
  playerNames?: PlayerNames,
): string {
  if (playerId === 'draw') return 'ничья';
  const nickname = playerNames?.[playerId]?.trim();
  if (nickname !== undefined && nickname.length > 0) return nickname;
  if (viewerId === undefined) return 'Игрок';
  return playerId === viewerId ? 'Вы' : 'Соперник';
}

export function choiceKindLabel(kind: string): string {
  switch (kind) {
    case 'enemyUnit':
      return 'вражеский боец';
    case 'anyUnit':
      return 'любой боец';
    case 'enemyPlayer':
      return 'вражеский игрок';
    case 'confirm':
      return 'подтверждение';
    case 'first':
      return 'первый вариант';
    case 'second':
      return 'второй вариант';
    case 'go':
      return 'продолжить';
    default:
      return 'доступный вариант';
  }
}

/**
 * A change as a player reads it: «+2», «−1» with the true minus sign (U+2212,
 * 03 §4.4), never the ASCII hyphen a number's own sign would print.
 */
/**
 * A cell in words: «локация 2, линия 1, сторона соперника». `T1-L0-B` is the
 * engine's address, not something to print or read aloud. Without the viewer's
 * side the label stops at the line; a string that is not a canonical id is
 * passed through untouched.
 */
export function cellDisplayLabel(cellId: string, viewerSide?: Side): string {
  let address: ReturnType<typeof parseCellId>;
  try {
    address = parseCellId(cellId);
  } catch {
    return cellId;
  }
  const where = `локация ${address.tableId + 1}, линия ${address.laneIndex + 1}`;
  if (viewerSide === undefined) return where;
  return `${where}, ${address.side === viewerSide ? 'ваша сторона' : 'сторона соперника'}`;
}

export function signedNumber(value: number): string {
  if (value > 0) return `+${value}`;
  if (value < 0) return `−${Math.abs(value)}`;
  return '0';
}
